import { z } from 'zod';
import { amountCents, email, isoDate, uuid } from './common.js';

/** Registration status as stored (`registration_status` enum). */
export const adminRegistrationStatus = z.enum([
  'pending',
  'paid',
  'cancelled',
  'refunded',
  'expired',
]);
export type AdminRegistrationStatus = z.infer<typeof adminRegistrationStatus>;

/**
 * Derived from the participant's licence and certificates, global to the platform: `approved` =
 * licence entered or a certificate approved and valid on the race day, `pending` = a certificate
 * waiting for review, `expired` = the approved certificate no longer covers the race day,
 * `rejected` = only rejected uploads, `missing` = nothing supplied.
 */
export const adminDocumentStatus = z.enum([
  'approved',
  'pending',
  'expired',
  'rejected',
  'missing',
]);
export type AdminDocumentStatus = z.infer<typeof adminDocumentStatus>;

/** Filters shared by the list (A-03) and the CSV export (A-06). */
export const adminRegistrationFilters = z.object({
  /** Substring of email, first or last name. */
  q: z.string().trim().max(100).optional(),
  formatId: uuid.optional(),
  status: adminRegistrationStatus.optional(),
  profile: z.enum(['complete', 'incomplete']).optional(),
  documents: adminDocumentStatus.optional(),
  /** Whether the registration's order carries the cancellation insurance (A-13). */
  insured: z.enum(['yes', 'no']).optional(),
});
export type AdminRegistrationFilters = z.infer<typeof adminRegistrationFilters>;

export const adminRegistrationsQuery = adminRegistrationFilters.extend({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminRegistrationsQuery = z.infer<typeof adminRegistrationsQuery>;

const person = z.object({
  id: uuid,
  email,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
});

export const adminRegistrationRow = z.object({
  id: uuid,
  status: adminRegistrationStatus,
  createdAt: isoDate,
  user: person,
  format: z.object({ id: uuid, name: z.string() }),
  priceCents: amountCents,
  bibNumber: z.number().int().nullable(),
  profileComplete: z.boolean(),
  documentStatus: adminDocumentStatus,
  insured: z.boolean(),
});
export type AdminRegistrationRow = z.infer<typeof adminRegistrationRow>;

/**
 * Per-format counts for the list header: `held` = pending orders, which hold a spot;
 * `insured` = paid registrations whose order carries the insurance.
 */
export const adminFormatCount = z.object({
  formatId: uuid,
  name: z.string(),
  capacity: z.number().int().nullable(),
  paid: z.number().int(),
  held: z.number().int(),
  insured: z.number().int(),
});

export const adminRegistrationsPage = z.object({
  rows: z.array(adminRegistrationRow),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  formats: z.array(adminFormatCount),
});
export type AdminRegistrationsPage = z.infer<typeof adminRegistrationsPage>;

export const adminNote = z.object({
  id: uuid,
  body: z.string(),
  createdAt: isoDate,
  author: z.object({ id: uuid, email }),
});
export type AdminNote = z.infer<typeof adminNote>;

export const createAdminNoteInput = z.object({ body: z.string().trim().min(1).max(2000) });
export type CreateAdminNoteInput = z.infer<typeof createAdminNoteInput>;

const invitation = z.object({
  id: uuid,
  email,
  status: z.enum(['sent', 'registered', 'bounced']),
  sentAt: isoDate,
  registeredAt: isoDate.nullable(),
});

export const adminRegistrationDetail = z.object({
  id: uuid,
  status: adminRegistrationStatus,
  createdAt: isoDate,
  priceCents: amountCents,
  bibNumber: z.number().int().nullable(),
  profileComplete: z.boolean(),
  documentStatus: adminDocumentStatus,
  format: z.object({ id: uuid, name: z.string() }),
  pricingTierName: z.string().nullable(),
  profile: person.extend({
    birthDate: z.string().nullable(),
    gender: z.string().nullable(),
    phone: z.string().nullable(),
    nationality: z.string().nullable(),
    clubName: z.string().nullable(),
    emergencyContactName: z.string().nullable(),
    emergencyContactPhone: z.string().nullable(),
    emergencyContactRelationship: z.string().nullable(),
    /** FFC/FFA licence and its review (null without a licence). */
    licence: z
      .object({
        federation: z.string(),
        number: z.string(),
        status: z.enum(['pending_review', 'approved', 'rejected']),
        rejectionReason: z.string().nullable(),
      })
      .nullable(),
    locale: z.string(),
    marketingOptIn: z.boolean(),
  }),
  /** Every order of this user for the event, the registration's own order first. */
  orders: z.array(
    z.object({
      id: uuid,
      status: z.enum(['pending', 'paid', 'failed', 'expired', 'refunded']),
      totalCents: amountCents,
      discountCents: amountCents,
      insured: z.boolean(),
      insuranceCents: amountCents,
      voucherCode: z.string().nullable(),
      createdAt: isoDate,
      paidAt: isoDate.nullable(),
      /** Stripe dashboard deep link; null while the PaymentIntent does not exist. */
      stripeUrl: z.string().url().nullable(),
      receiptUrl: z.string().nullable(),
      registrations: z.array(
        z.object({ id: uuid, formatName: z.string(), priceCents: amountCents }),
      ),
    }),
  ),
  invitationsSent: z.array(invitation),
  /** Invitation(s) this person received, and who sent it. */
  invitationsReceived: z.array(invitation.extend({ referrerEmail: email.nullable() })),
  /** The participant's certificates (global to the platform), previewed on demand (A-05). */
  documents: z.array(
    z.object({
      id: uuid,
      kind: z.enum(['medical_certificate', 'pps_attestation', 'licence']),
      status: z.enum(['pending_review', 'approved', 'rejected']),
      fileName: z.string(),
      /** Last valid day of a certificate (P-12). */
      expiresOn: z.string().nullable(),
      uploadedAt: isoDate,
      reviewedAt: isoDate.nullable(),
      rejectionReason: z.string().nullable(),
    }),
  ),
  notes: z.array(adminNote),
});
export type AdminRegistrationDetail = z.infer<typeof adminRegistrationDetail>;
